import styled from "styled-components"
import Header from "../../Common/Header"
import Footer from "../../Common/Footer"
import { futuristicListMarker } from "@components/base/listMarkers"

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 6vw, 4rem);
  background: #f5f8fb;
`

const Hero = styled.section`
  position: relative;
  min-height: min(720px, 76vh);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(5rem, 10vw, 7rem) 1.5rem;
  color: #ffffff;
  background: url("/img/cta-management.jpg") center/cover no-repeat;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(140deg, rgba(9, 36, 66, 0.92) 10%, rgba(18, 82, 133, 0.72) 55%, rgba(32, 127, 190, 0.6) 100%);
    z-index: 0;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(1080px, 92vw);
  display: grid;
  gap: 1.6rem;
  text-align: center;
  backdrop-filter: blur(6px);
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.3rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.18);
  letter-spacing: 0.12em;
  font-size: 0.78rem;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.6rem, 6vw, 4rem);
  line-height: 1.1;
  color: #fff;
`

const HeroSubtitle = styled.p`
  margin: 0 auto;
  max-width: 760px;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
`

const Article = styled.article`
  width: min(960px, 92vw);
  margin: 0 auto;
  background: #ffffff;
  border-top: 4px solid #337abd;
  border-radius: 2px;
  padding: clamp(2rem, 5vw, 3rem);
  box-shadow: 0 24px 60px rgba(13, 65, 114, 0.12);
  display: grid;
  gap: 1.6rem;
  color: #1b2e41;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  color: #103c68;
`

const Paragraph = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.8;
`

const BulletList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.8rem;
  font-size: 1.05rem;
  line-height: 1.7;

  li {
    ${futuristicListMarker};
    color: #1b2e41;
  }
`

const HighlightCard = styled.div`
  background: rgba(99, 195, 255, 0.1);
  border-radius: 2px;
  padding: 1.5rem;
  border: 1px solid rgba(99, 195, 255, 0.25);
  display: grid;
  gap: 0.6rem;
`

const OptimiserChaletsLocatifs = () => {
  return (
    <PageWrapper>
      <Header />
      <main id="contenu-principal">
        <Hero>
          <HeroContent>
            <HeroBadge>Gestion de locations saisonnières</HeroBadge>
            <HeroTitle>Mieux structurer la gestion de votre chalet locatif</HeroTitle>
            <HeroSubtitle>Trois volets à suivre : les tarifs, l’expérience des voyageurs et la visibilité de l’annonce.</HeroSubtitle>
          </HeroContent>
        </Hero>

        <Article>
          <Paragraph>
            Une location saisonnière demande des décisions répétées : ouvrir les bonnes dates, ajuster le tarif, préparer les lieux et répondre aux
            voyageurs. Un processus simple et documenté permet de comparer les périodes et de corriger ce qui fonctionne moins bien.
          </Paragraph>

          <SectionTitle>1. Ajuster votre tarification en temps réel</SectionTitle>
          <Paragraph>
            Appuyez-vous sur des outils comme PriceLabs ou Beyond Pricing pour ajuster vos tarifs selon la saison, les événements régionaux et la
            demande. Combinez ces données avec votre connaissance du territoire pour fixer des minimums et des durées de séjour adaptées.
          </Paragraph>

          <SectionTitle>2. Concevoir une expérience « destination »</SectionTitle>
          <HighlightCard>
            <strong>Des détails utiles aux voyageurs :</strong>
            <BulletList>
              <li>Guide d'accueil numérique avec recommandations locales (sentiers, fournisseurs, restos à Neuville et Donnacona).</li>
              <li>Consignes claires pour les équipements, inventaire à jour et produits d’arrivée adaptés à la durée du séjour.</li>
              <li>Service concierge léger : partenaires pour activités, chef à domicile, location d'équipement.</li>
            </BulletList>
          </HighlightCard>

          <SectionTitle>3. Miser sur une visibilité hyper-locale</SectionTitle>
          <Paragraph>
            Au-delà des grandes plateformes, évaluez les canaux réellement utilisés par votre clientèle et les partenariats touristiques pertinents.
            Gardez les descriptions, photos, disponibilités et règles cohérentes sur tous les canaux.
          </Paragraph>

          <Paragraph>
            Suivez séparément le tarif moyen, les nuits réservées, les frais d’exploitation et les commentaires récurrents. Ces données vous aideront
            à décider quels ajustements méritent d’être maintenus.
          </Paragraph>
        </Article>
      </main>
      <Footer />
    </PageWrapper>
  )
}

export default OptimiserChaletsLocatifs
